import {evaluate, parseVariables} from '../../lib/json-logic-utils';
import {AGGREGATE_OPERATORS} from '../../components/JsonLogicBuilder/aggregations';
import {isQuestionDeleted, parseDefaultAnswerFormula} from '../FormBuilderScreen/shared';

// Editing an answer on the dashboard does not recalculate the formula default answers that read it, the mobile app
// only works them out while a row is being filled in. This works them out again the same way the app does (see
// lib/formulaDefaultAnswer.ts and lib/formulaAggregation.ts in the mobile repo), so the two have to be kept in step.

const FORMULA_QUESTION_TYPES = ['NUMERIC', 'MEASURED_NUMERIC'];

// Formula results are rounded the same way the app rounds them.
const DECIMAL_PLACES = 1000;

// The row questions whose formula default answer reads `question`, directly or through another formula. Sheet setup
// is answered once for every row, so an edit to it would need every row recalculated and is left to the warning.
export function getRecalculatedQuestions(question, questions) {
  if (!question || question.context) {
    return [];
  }

  const formulaQuestions = (questions || []).filter(q => !q.context && !isQuestionDeleted(q) && getFormula(q));
  const changedIds = new Set([question.id]);
  let added = true;

  // A formula can read one that comes after it in the form, so keep going until nothing new turns up.
  while (added) {
    added = false;

    for (const q of formulaQuestions) {
      if (!changedIds.has(q.id) && parseVariables(getFormula(q)).some(id => changedIds.has(id))) {
        changedIds.add(q.id);
        added = true;
      }
    }
  }

  return formulaQuestions.filter(q => q.id !== question.id && changedIds.has(q.id));
}

export function hasAggregate(expression) {
  if (Array.isArray(expression)) {
    return expression.some(hasAggregate);
  }

  if (expression === null || typeof expression !== 'object') {
    return false;
  }

  const operator = Object.keys(expression)[0];
  return AGGREGATE_OPERATORS.indexOf(operator) >= 0 || hasAggregate(expression[operator]);
}

// The answers to save so the formulas that read the edited answer match it again, as a list of
// {row, question, values}. `rows` has to be every row of the sheet, not only the ones a filter left on screen, since
// an aggregation totals all of them. A row is a sheet row as the API returns it, and `editedRow` is one of them.
//
// A blank formula answer is only filled in on the edited row, where it is usually blank because the input being fixed
// was missing, and only when the formula's condition shows it. Other rows only have an existing answer recalculated:
// the app leaves a formula hidden by a condition unanswered, and a row submitted before the formula was added never
// had one.
export function getRecalculations({questions, rows, contextAnswers, editedRow, question, values}) {
  const recalculated = getRecalculatedQuestions(question, questions);

  if (recalculated.length === 0) {
    return [];
  }

  // Rows load a page at a time, so one that moved between pages while they loaded can come back twice. Counting it
  // twice would put it in every later total twice.
  const seen = new Set();
  const ordered = (rows || [])
    .filter(row => !seen.has(row.id) && seen.add(row.id))
    .sort((a, b) => getCreatedTime(a) - getCreatedTime(b));
  const editedIndex = ordered.findIndex(row => row.id === editedRow.id);

  if (editedIndex < 0) {
    return [];
  }

  // An aggregation reads the rows up to and including its own, so an edit reaches every row after the edited one.
  // Without one, only the edited row reads the edited answer.
  const aggregates = recalculated.some(q => hasAggregate(getFormula(q)));
  const lastIndex = aggregates ? ordered.length - 1 : editedIndex;

  // The answers each row holds once the edit and every recalculation before it are applied, so a later row totals
  // the new values of the rows before it.
  const answersByRow = ordered.map(row => row.answers || []);
  answersByRow[editedIndex] = replaceValues(answersByRow[editedIndex], question.id, values);

  const changes = [];

  for (let i = editedIndex; i <= lastIndex; i++) {
    for (const q of recalculated) {
      const current = answersByRow[i].filter(answer => answer.questionId === q.id);

      const rowAnswers = [...answersByRow[i], ...(contextAnswers || [])];

      if (current.length === 0 && (i !== editedIndex || !isShown(q, rowAnswers))) {
        continue;
      }

      const value = evaluateFormula(getFormula(q), rowAnswers, answersByRow.slice(0, i + 1));
      const nextValues = value === null ? [] : [value];

      if (current.length === nextValues.length && current.every((answer, index) => String(answer.value) === nextValues[index])) {
        continue;
      }

      answersByRow[i] = replaceValues(answersByRow[i], q.id, nextValues);
      changes.push({row: ordered[i], question: q, values: nextValues});
    }
  }

  return changes;
}

// The formula result as an answer value, or null when it cannot produce a number, which the app leaves blank.
// `rows` holds the answers of every row up to and including the one being worked out.
export function evaluateFormula(formula, answers, rows) {
  const resolved = resolveAggregates(formula, rows);

  if (resolved === null) {
    return null;
  }

  const result = evaluate(resolved.value, getVariables(answers));
  const numericResult = typeof result === 'number' ? result : parseFloat(result);

  if (!isFinite(numericResult)) {
    return null;
  }

  return String(Math.round(numericResult * DECIMAL_PLACES) / DECIMAL_PLACES);
}

// json-logic does not know the aggregate operators, so each one is replaced with the number it stands for. Wrapped
// so an operand left unfinished (null) stays apart from an aggregation that had nothing to total.
function resolveAggregates(node, rows) {
  if (Array.isArray(node)) {
    const values = [];

    for (const item of node) {
      const resolved = resolveAggregates(item, rows);

      if (resolved === null) {
        return null;
      }

      values.push(resolved.value);
    }

    return {value: values};
  }

  if (node === null || typeof node !== 'object') {
    return {value: node};
  }

  const operator = Object.keys(node)[0];

  if (!operator) {
    return {value: node};
  }

  if (AGGREGATE_OPERATORS.indexOf(operator) >= 0) {
    const result = aggregate(operator, getTarget(node[operator]), rows);
    return result === null ? null : {value: result};
  }

  const resolved = resolveAggregates(node[operator], rows);
  return resolved === null ? null : {value: {[operator]: resolved.value}};
}

function getTarget(operands) {
  const operand = Array.isArray(operands) ? operands[0] : operands;

  if (!operand || typeof operand !== 'object') {
    return null;
  }

  const target = Array.isArray(operand.var) ? operand.var[0] : operand.var;
  return typeof target === 'string' && target ? target : null;
}

function aggregate(operator, questionId, rows) {
  if (!questionId) {
    return null;
  }

  const values = [];

  for (const answers of rows) {
    for (const answer of answers || []) {
      const value = answer && answer.questionId === questionId ? parseFloat(answer.value) : NaN;

      // A row that skipped the question is left out, and a multi answer question adds each of its values.
      if (isFinite(value)) {
        values.push(value);
      }
    }
  }

  // A total of no rows is not 0, so the formula has no value yet.
  if (values.length === 0) {
    return null;
  }

  const sum = values.reduce((total, value) => total + value, 0);

  switch (operator) {
    case 'agg_sum':
      return sum;
    case 'agg_avg':
      return sum / values.length;
    case 'agg_min':
      return Math.min(...values);
    case 'agg_max':
      return Math.max(...values);
    default:
      return null;
  }
}

// Whether the app shows the question for these answers, worked out as lib/question.js shouldShowQuestion does there.
function isShown(question, answers) {
  if (!question.condition) {
    return true;
  }

  try {
    return !!evaluate(JSON.parse(question.condition), getVariables(answers));
  }
  catch (e) {
    return false;
  }
}

// The app hands json-logic the answers as the strings they are stored as, rather than parsing them by question type
// as json-logic-utils.getVariables does, so this does the same to get the same results.
function getVariables(answers) {
  return answers.reduce((map, answer) => {
    const previous = map[answer.questionId];

    if (previous === undefined) {
      map[answer.questionId] = answer.value;
    }
    else {
      map[answer.questionId] = Array.isArray(previous) ? [...previous, answer.value] : [previous, answer.value];
    }

    return map;
  }, {});
}

function getFormula(question) {
  if (FORMULA_QUESTION_TYPES.indexOf(question.type) < 0) {
    return null;
  }

  return parseDefaultAnswerFormula((question.defaultAnswers || [])[0]);
}

function replaceValues(answers, questionId, values) {
  return answers
    .filter(answer => answer.questionId !== questionId)
    .concat(values.map(value => ({questionId, value})));
}

function getCreatedTime(row) {
  const created = row && row.dates && row.dates.created;
  const time = created ? new Date(created).getTime() : 0;

  return isFinite(time) ? time : 0;
}
